import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays, ChevronRight, Save, Layers, BookOpen,
  GitBranch, Activity, Dumbbell, Clock, AlertTriangle, X,
  Search, Plus, Trash2, CheckCircle2, Settings2, Wand2, Sparkles,
} from "lucide-react";
import InfoButton from "@/components/periodisation/InfoButton";
import PeriodisationTimeline from "@/components/periodisation/PeriodisationTimeline";

/* ---- level definitions (focused selector order) ---- */
const LEVEL_TABS = [
  ["multiannual", "Multiannual"],
  ["annual", "Annual"],
  ["macrocycle", "Macrocycle"],
  ["mesocycle", "Mesocycle"],
  ["microcycle", "Microcycle"],
  ["all", "All levels"],
];
const TYPE_LABEL = {
  multiannual: "Multiannual plan", annual: "Annual plan", macrocycle: "Macrocycle",
  period: "Training period", mesocycle: "Mesocycle", microcycle: "Microcycle",
  day: "Training day", session: "Session", unit: "Training unit",
};
// Which reference group drives the type-specific select at each level.
const LEVEL_FIELD = {
  macrocycle: { field: "training_approach", group: "training_approach", label: "Training approach" },
  period: { field: "phase_type", group: "phase_type", label: "Phase" },
  mesocycle: { field: "meso_role", group: "meso_role", label: "Mesocycle role" },
  microcycle: { field: "micro_type", group: "micro_type", label: "Microcycle type" },
};

const fmtDate = (s) => (s ? new Date(s + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "—");
const dayLabel = (s) => (s ? new Date(s + "T00:00:00Z").toLocaleDateString("en-GB", { weekday: "long", timeZone: "UTC" }) : "Day");
const durationDays = (a, b) => (a && b ? Math.round((new Date(b) - new Date(a)) / 86400000) + 1 : null);

/* ---- calendar-led visual helpers (Stage B) ---- */
const addDaysISO = (iso, n) => {
  if (!iso) return iso;
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const datePct = (dateStr, startStr, totalDays) => {
  if (!dateStr || !startStr || !totalDays) return 0;
  const d = Math.round((new Date(dateStr + "T00:00:00Z") - new Date(startStr + "T00:00:00Z")) / 86400000);
  return Math.max(0, Math.min(100, (d / totalDays) * 100));
};
const axisTicks = (start, end, n = 4) => {
  if (!start || !end) return [];
  const total = durationDays(start, end) || 1;
  const out = [];
  for (let i = 0; i < n; i++) {
    const pct = n === 1 ? 0 : (i / (n - 1)) * 100;
    const d = addDaysISO(start, Math.round((pct / 100) * (total - 1)));
    out.push({ pct, label: new Date(d + "T00:00:00Z").toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" }) });
  }
  return out;
};
// Deterministic, evidence-neutral colour per child block (visual grouping only).
const PHASE_PALETTE = [
  { bg: "rgba(59,130,246,0.18)", border: "#3b82f6", text: "#93c5fd" },
  { bg: "rgba(168,85,247,0.18)", border: "#a855f7", text: "#d8b4fe" },
  { bg: "rgba(245,158,11,0.18)", border: "#f59e0b", text: "#fde047" },
  { bg: "rgba(239,68,68,0.18)", border: "#ef4444", text: "#fca5a5" },
  { bg: "rgba(16,185,129,0.18)", border: "#10b981", text: "#6ee7b7" },
  { bg: "rgba(236,72,153,0.18)", border: "#ec4899", text: "#f9a8d4" },
];
const blockColor = (node, idx) => PHASE_PALETTE[idx % PHASE_PALETTE.length];

export default function Periodisation({ patientId, patientName, programmeId, programme: programmeProp }) {
  const nav = useNavigate();
  const [programme, setProgramme] = useState(programmeProp === undefined ? undefined : (programmeProp || null));
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [flags, setFlags] = useState(null);
  const [level, setLevel] = useState("microcycle");
  const [selectedByType, setSelectedByType] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [showPrinciples, setShowPrinciples] = useState(false);
  const [dateModal, setDateModal] = useState(null); // {node}
  const [removeTarget, setRemoveTarget] = useState(null); // node pending removal
  const [publishReview, setPublishReview] = useState(null); // publish preview payload
  const [loadSummary, setLoadSummary] = useState(null);
  const [progress, setProgress] = useState({});
  const [showGenerate, setShowGenerate] = useState(false);
  const editorRef = useRef(null);

  const refreshProgress = async (pid) => {
    if (!pid) return;
    try { const r = await api.get(`/periodisation/${pid}/progress`); setProgress(r.data.progress || {}); }
    catch { /* non-blocking — badges just stay hidden */ }
  };

  // Timeline block click → select that node in the editor below and scroll to it.
  const selectBlock = (node) => {
    if (!node) return;
    const selectable = ["multiannual", "annual", "macrocycle", "mesocycle", "microcycle"];
    if (selectable.includes(node.type)) {
      setLevel(node.type);
      setSelectedByType((p) => ({ ...p, [node.type]: node.id }));
    } else if (node.type === "period") {
      const parent = nodeById(node.parent_id);
      if (parent) { setLevel("macrocycle"); setSelectedByType((p) => ({ ...p, macrocycle: parent.id })); }
    }
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  /* ---- load programme → meta/flags → plan ---- */
  useEffect(() => {
    (async () => {
      try {
        const [m, fl] = await Promise.all([
          api.get(`/periodisation/meta`),
          api.get(`/periodisation/flags`),
        ]);
        setMeta(m.data); setFlags(fl.data);
        let prog = programmeProp;
        if (!prog) {
          const pr = await api.get(`/programmes/patient/${patientId}`);
          prog = pr.data || null;
        }
        setProgramme(prog || null);
        if (prog?.id) {
          const p = await api.get(`/periodisation/by-programme/${programmeId || prog.id}`);
          setPlan(p.data);
          refreshProgress(p.data?.id);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load periodisation.");
        setProgramme((v) => (v === undefined ? null : v));
      }
    })();
  }, [patientId, programmeId]);

  const nodes = plan?.nodes || [];
  const byType = (t) => nodes.filter((n) => n.type === t);
  const childrenOf = (id) => nodes.filter((n) => n.parent_id === id);
  const nodeById = (id) => nodes.find((n) => n.id === id);

  // Default-select the first node of each level once the plan loads.
  useEffect(() => {
    if (!plan) return;
    const next = {};
    ["multiannual", "annual", "macrocycle", "mesocycle", "microcycle"].forEach((t) => {
      next[t] = selectedByType[t] || byType(t)[0]?.id || null;
    });
    setSelectedByType((prev) => ({ ...next, ...prev }));
    // eslint-disable-next-line
  }, [plan]);

  const selectedNode = level !== "all" ? nodeById(selectedByType[level]) : null;

  // Breadcrumb ancestors of a node.
  const ancestors = (node) => {
    const chain = [];
    let cur = node;
    while (cur?.parent_id) { cur = nodeById(cur.parent_id); if (cur) chain.unshift(cur); }
    return chain;
  };

  /* ---- node mutation (local, dirty) ---- */
  const patchNode = (id, patch) => {
    setPlan((p) => ({ ...p, nodes: p.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/periodisation/${plan.id}`, { version: plan.version, nodes: plan.nodes });
      setPlan(res.data.plan);
      setWarnings(res.data.warnings || []);
      setDirty(false);
      refreshProgress(res.data.plan?.id || plan.id);
      toast.success("Periodisation saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. The plan may have changed — refresh.");
    } finally { setSaving(false); }
  };

  const openLoad = async (node) => {
    setLoadSummary({ loading: true, node });
    try {
      const res = await api.get(`/periodisation/${plan.id}/load-summary`, { params: { node_id: node.id } });
      setLoadSummary({ ...res.data, node });
    } catch (e) {
      setLoadSummary(null);
      toast.error("Could not compute load summary.");
    }
  };

  /* ---- add / duplicate / remove / publish ---- */
  const collectSubtreeIds = (id) => {
    const ids = new Set([id]); let added = true;
    while (added) { added = false; nodes.forEach((n) => { if (n.parent_id && ids.has(n.parent_id) && !ids.has(n.id)) { ids.add(n.id); added = true; } }); }
    return ids;
  };
  const addChild = (parentNode, childType) => {
    const nid = (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()));
    const siblings = nodes.filter((n) => n.parent_id === parentNode.id && n.type === childType);
    const single = ["day", "session", "unit"].includes(childType);
    const node = { id: nid, type: childType, parent_id: parentNode.id, name: `New ${childType}`, display_name: null, objective: null, start_date: parentNode.start_date || null, end_date: single ? (parentNode.start_date || null) : (parentNode.end_date || null), order: siblings.length + 1 };
    if (childType === "unit") { node.exercise_refs = []; node.unit_category = ""; }
    if (childType === "session") { node.planned_minutes = 50; node.target_rpe = null; node.activity_type = "strength"; }
    setPlan((p) => ({ ...p, nodes: [...p.nodes, node] }));
    setDirty(true);
    toast.message(`Added a ${childType}. Set its dates, then Save.`);
    return nid;
  };
  const duplicateNode = async (node) => {
    if (dirty) { toast.warning("Save your changes before duplicating."); return; }
    try {
      const res = await api.post(`/periodisation/${plan.id}/duplicate-node`, { node_id: node.id });
      setPlan(res.data.plan); setWarnings(res.data.warnings || []);
      toast.success(`Duplicated as draft${res.data.prescriptions_cloned ? ` · ${res.data.prescriptions_cloned} new prescription copies` : ""}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate."); }
  };
  const confirmRemove = () => {
    const ids = collectSubtreeIds(removeTarget.id);
    const remaining = plan.nodes.filter((n) => !ids.has(n.id));
    setPlan((p) => ({ ...p, nodes: remaining }));
    setSelectedByType((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((t) => { if (next[t] && ids.has(next[t])) next[t] = remaining.find((n) => n.type === t)?.id || null; });
      return next;
    });
    setDirty(true); setRemoveTarget(null);
    toast.message("Removed from the plan — click Save to persist. Exercise prescriptions and completed sessions are untouched.");
  };
  const reloadPlan = async () => {
    try { const r = await api.get(`/periodisation/by-programme/${programme.id}`); setPlan(r.data); setDirty(false); } catch { /* noop */ }
  };
  const unpublish = async () => {
    try { const res = await api.post(`/periodisation/${plan.id}/unpublish`); setPlan((p) => ({ ...p, status: res.data.status })); toast.success("Returned to draft (hidden from the athlete)."); }
    catch { toast.error("Could not change publish state."); }
  };
  const openReview = async () => {
    if (dirty) { toast.warning("Save your changes before reviewing for publish."); return; }
    setPublishReview({ loading: true });
    try { const res = await api.get(`/periodisation/${plan.id}/publish-preview`); setPublishReview(res.data); }
    catch (e) { setPublishReview(null); toast.error("Could not build the review."); }
  };
  const confirmPublish = async () => {
    try {
      const res = await api.post(`/periodisation/${plan.id}/publish`, { preview_token: publishReview.preview_token });
      setPlan((p) => ({ ...p, status: "active" })); setPublishReview(null);
      toast.success(`Published — ${res.data.scheduled_exercises} exercise(s) scheduled into the athlete's My Plan.`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning("The draft changed — refreshing the review.");
        await reloadPlan();
        setPublishReview({ loading: true });
        try { const r = await api.get(`/periodisation/${plan.id}/publish-preview`); setPublishReview(r.data); } catch { setPublishReview(null); }
      } else toast.error(e?.response?.data?.detail || "Could not publish.");
    }
  };

  /* ---- Stage C: apply a generated rule-based draft into the planner (unsaved) ---- */
  const applyGeneratedDraft = (draftNodes) => {
    setPlan((p) => ({ ...p, nodes: draftNodes }));
    setSelectedByType({});
    setLevel("annual");
    setDirty(true);
    setShowGenerate(false);
    toast.success("Draft generated — review the plan, then Save to keep it.");
  };

  /* ---- embedded exercise builder (Stage B) — in-place programme edits ---- */
  const addPlanExercise = async (unitId, libEx) => {
    if (dirty) { toast.warning("Save your plan changes before adding exercises."); return; }
    try {
      const res = await api.post(`/periodisation/${plan.id}/exercises`, {
        exercise_id: libEx.id, sets: libEx.default_sets ?? null, reps: libEx.default_reps ?? null,
        rest_seconds: 60, frequency_per_week: 3, unit_id: unitId,
      });
      const row = res.data.programme_exercise;
      setProgramme((pr) => ({ ...(pr || {}), exercises: [...((pr || {}).exercises || []), row] }));
      setPlan((p) => ({ ...p, nodes: res.data.plan?.nodes || p.nodes, version: res.data.plan?.version ?? p.version }));
      toast.success(`Added ${row.exercise?.name || "exercise"} to this unit.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the exercise."); }
  };
  const updatePlanExercise = async (peId, patch) => {
    try {
      const res = await api.patch(`/periodisation/${plan.id}/exercises/${peId}`, patch);
      const row = res.data.programme_exercise;
      setProgramme((pr) => ({ ...(pr || {}), exercises: ((pr || {}).exercises || []).map((e) => (e.id === peId ? row : e)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the prescription."); }
  };
  const removePlanExercise = async (peId) => {
    if (dirty) { toast.warning("Save your plan changes before removing exercises."); return; }
    try {
      const res = await api.delete(`/periodisation/${plan.id}/exercises/${peId}`);
      setProgramme((pr) => ({ ...(pr || {}), exercises: ((pr || {}).exercises || []).filter((e) => e.id !== peId) }));
      setPlan((p) => ({ ...p, nodes: res.data.plan?.nodes || p.nodes, version: res.data.plan?.version ?? p.version }));
      toast.message("Removed from the unit and programme. Completed history is untouched.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove the exercise."); }
  };

  if (programme === undefined || (programme && !plan)) {
    return <div className="fl-card p-8 text-center text-sm text-zinc-400" data-testid="periodisation-loading">Loading periodisation…</div>;
  }
  if (!programme) {
    return (
      <div className="fl-card p-8 text-center" data-testid="periodisation-no-programme">
        <div className="fl-heading text-lg">No active programme</div>
        <p className="text-sm text-zinc-400 mt-2">Create a programme for this athlete in <b>Cases &amp; Programmes</b> first — the planner organises that programme’s exercises.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="periodisation-root">
      {/* ---- header + level selector ---- */}
      <div className="fl-card fl-card-elevated p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="fl-label text-xs text-zinc-400">Periodisation · {patientName}</div>
            <h2 className="fl-heading text-xl mt-0.5">{programme.title}</h2>
            <div className="text-[11px] text-zinc-500 mt-1">One connected plan. Choose the level you need. Dates and doses are clinician-controlled.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${plan?.status === "active" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-zinc-400 border-white/15"}`} data-testid="periodisation-status">
              {plan?.status === "active" ? "Published" : "Draft"}
            </span>
            <button onClick={() => setShowPrinciples(true)} className="fl-btn-ghost text-xs" data-testid="periodisation-principles-btn">
              <BookOpen size={13} /> Principles &amp; models
            </button>
            <button onClick={() => { if (dirty) { toast.warning("Save or discard your current changes before generating a draft."); return; } setShowGenerate(true); }} className="fl-btn-ghost text-xs" data-testid="periodisation-generate-btn">
              <Wand2 size={13} /> Generate draft
            </button>
            <button onClick={openReview} className="fl-btn-ghost text-xs" data-testid="periodisation-publish-btn">
              {plan?.status === "active" ? "Review & re-publish" : "Publish to athlete"}
            </button>
            {plan?.status === "active" && (
              <button onClick={unpublish} className="fl-btn-ghost text-xs" data-testid="periodisation-unpublish-btn">Unpublish</button>
            )}
            <button onClick={save} disabled={!dirty || saving} className="fl-btn text-xs disabled:opacity-50" data-testid="periodisation-save-btn">
              <Save size={13} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-1 rounded-full p-1 w-full sm:w-fit overflow-x-auto no-scrollbar" style={{ background: "var(--fl-panel-2, rgba(255,255,255,0.04))" }} data-testid="periodisation-level-tabs">
          {LEVEL_TABS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setLevel(k)}
              data-testid={`level-tab-${k}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition flex items-center gap-1 whitespace-nowrap shrink-0 ${level === k ? "bg-[#f47a20] text-black" : "text-zinc-400 hover:text-zinc-100"}`}
            >
              {k === "all" ? <Layers size={12} /> : null}{label}
            </button>
          ))}
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="fl-card p-4 border" style={{ borderColor: "rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.06)" }} data-testid="periodisation-warnings">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold"><AlertTriangle size={15} /> Review needed</div>
          <ul className="mt-2 space-y-1 text-xs text-amber-200/90 list-disc pl-5">
            {warnings.map((w, i) => <li key={i}>{w.message}</li>)}
          </ul>
        </div>
      )}

      {plan && level === "all" && (
        <PeriodisationTimeline
          plan={plan}
          level={level}
          selectedByType={selectedByType}
          onSelectNode={selectBlock}
        />
      )}

      <div ref={editorRef} />

      {/* ---- focused view ---- */}
      {level !== "all" && (
        <FocusedLevel
          key={selectedByType[level] || level}
          level={level}
          nodes={nodes}
          planId={plan.id}
          dirty={dirty}
          selectedId={selectedByType[level]}
          onSelectNode={(id) => setSelectedByType((p) => ({ ...p, [level]: id }))}
          node={selectedNode}
          ancestors={selectedNode ? ancestors(selectedNode) : []}
          childrenOf={childrenOf}
          nodeById={nodeById}
          meta={meta}
          programme={programme}
          patientId={patientId}
          flags={flags}
          onAddPlanExercise={addPlanExercise}
          onUpdatePlanExercise={updatePlanExercise}
          onRemovePlanExercise={removePlanExercise}
          onPatch={patchNode}
          onDrill={(childType, childId) => { setLevel(childType); setSelectedByType((p) => ({ ...p, [childType]: childId })); }}
          onDateEdit={(node) => { if (dirty) { toast.warning("Save your changes before editing dates."); return; } setDateModal({ node }); }}
          onLoad={openLoad}
          onDuplicate={duplicateNode}
          onRemove={(node) => setRemoveTarget(node)}
          onAddChild={addChild}
          progress={progress}
          nav={nav}
        />
      )}

      {/* ---- all levels tab shows the timeline only (no node cards / editor / breadcrumbs) ---- */}

      {dateModal && (
        <DateChangeModal
          plan={plan}
          node={dateModal.node}
          onClose={() => setDateModal(null)}
          onApplied={(res) => { setPlan(res.plan); setWarnings(res.warnings || []); setDateModal(null); toast.success("Dates updated."); }}
        />
      )}
      {loadSummary && <LoadModal data={loadSummary} onClose={() => setLoadSummary(null)} />}
      {publishReview && <PublishReviewModal data={publishReview} onBack={() => setPublishReview(null)} onConfirm={confirmPublish} />}
      {showPrinciples && <PrinciplesModal meta={meta} onClose={() => setShowPrinciples(false)} />}
      {showGenerate && (
        <GenerateDraftModal
          plan={plan}
          onClose={() => setShowGenerate(false)}
          onApply={applyGeneratedDraft}
        />
      )}
      {removeTarget && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }} onClick={() => setRemoveTarget(null)} data-testid="remove-modal">
          <div className="fl-card-elevated w-full max-w-md p-5" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-300 font-semibold"><AlertTriangle size={16} /> Remove from plan?</div>
            <p className="text-sm text-zinc-300 mt-2">This removes <b>{removeTarget.display_name || removeTarget.name}</b> and its <b>{collectSubtreeIds(removeTarget.id).size - 1}</b> child item(s) from the periodisation structure.</p>
            <p className="text-[11px] text-zinc-500 mt-2">This is a planner association only. It does <b>not</b> delete any exercise prescription, exercise-library definition, completed session or signed record. Click Save afterwards to persist.</p>
            <div className="flex gap-2 mt-4">
              <button className="fl-btn text-sm flex-1 justify-center" onClick={confirmRemove} data-testid="remove-confirm">Remove</button>
              <button className="fl-btn-ghost text-sm" onClick={() => setRemoveTarget(null)} data-testid="remove-cancel">Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ================= Focused level ================= */
/* ================= Stage progress badge (done / total exercises) ================= */
function ProgressBadge({ p, className = "" }) {
  if (!p || !p.total) return null;
  const full = p.done >= p.total;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none ${className}`}
      style={{ background: full ? "rgba(34,197,94,0.18)" : "rgba(255,255,255,0.08)", color: full ? "#4ade80" : "#a1a1aa" }}
      data-testid="stage-progress-badge"
      title={`${p.done} of ${p.total} exercise(s) completed in this period`}
    >
      <CheckCircle2 size={9} />{p.done}/{p.total}
    </span>
  );
}

function FocusedLevel({ level, nodes, selectedId, onSelectNode, node, ancestors, childrenOf, nodeById, meta, programme, patientId, flags, onPatch, onDrill, onDateEdit, onLoad, onDuplicate, onRemove, onAddChild, onAddPlanExercise, onUpdatePlanExercise, onRemovePlanExercise, progress, nav }) {
  const sameType = nodes.filter((n) => n.type === level);
  const [editOpen, setEditOpen] = useState(false);
  if (!node) {
    return <div className="fl-card p-8 text-center text-sm text-zinc-400" data-testid="focused-empty">No {TYPE_LABEL[level]} in this plan yet.</div>;
  }
  const fieldCfg = LEVEL_FIELD[level];
  const refGroup = fieldCfg ? (meta?.reference?.[fieldCfg.group] || []) : [];
  const currentEntry = fieldCfg ? refGroup.find((r) => r.key === node[fieldCfg.field]) : null;

  return (
    <div className="space-y-4" data-testid={`focused-${level}`}>
      {/* breadcrumb + sibling picker */}
      <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
        <div className="flex items-center gap-1 text-zinc-500 flex-wrap" data-testid="periodisation-breadcrumb">
          {ancestors.map((a) => (
            <span key={a.id} className="flex items-center gap-1">
              <button className="hover:text-zinc-200" onClick={() => onDrill(a.type, a.id)}>{a.display_name || a.name}</button>
              <ChevronRight size={11} />
            </span>
          ))}
          <span className="text-zinc-200 font-semibold">{node.display_name || node.name}</span>
        </div>
        {sameType.length > 1 && (
          <select className="fl-input text-xs py-1" value={selectedId || ""} onChange={(e) => onSelectNode(e.target.value)} data-testid="periodisation-sibling-select">
            {sameType.map((n) => <option key={n.id} value={n.id}>{n.display_name || n.name}</option>)}
          </select>
        )}
      </div>

      {/* editor card */}
      <div className="fl-card fl-card-elevated p-4 sm:p-5" data-testid="node-editor">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#f47a20]">{TYPE_LABEL[level]}</div>
            <div className="fl-heading text-lg truncate flex items-center gap-2">{node.display_name || node.name}<ProgressBadge p={progress?.[node.id]} /></div>
            <div className="text-xs text-zinc-500 mt-0.5" data-testid="node-dates">{fmtDate(node.start_date)} – {fmtDate(node.end_date)} · {durationDays(node.start_date, node.end_date) ?? "—"} days</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button className="fl-btn-ghost text-xs" onClick={() => onDuplicate(node)} data-testid="node-duplicate-btn">Duplicate</button>
            {node.parent_id && <button className="fl-btn-ghost text-xs text-red-300" onClick={() => onRemove(node)} data-testid="node-remove-btn">Remove</button>}
            <button className="fl-btn-ghost text-xs" onClick={() => onLoad(node)} data-testid="node-load-btn"><Activity size={12} /> Load &amp; volume</button>
            <button className="fl-btn-ghost text-xs" onClick={() => setEditOpen((v) => !v)} data-testid="node-editor-toggle"><Settings2 size={12} /> {editOpen ? "Hide details" : "Edit details"}</button>
          </div>
        </div>

        {editOpen && (<div className="mt-1">
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-[11px] text-zinc-500">Name / type</span>
            <input className="fl-input w-full text-sm mt-1" value={node.name || ""} onChange={(e) => onPatch(node.id, { name: e.target.value })} data-testid="node-name" />
          </label>
          <label className="block">
            <span className="text-[11px] text-zinc-500">Display label (optional)</span>
            <input className="fl-input w-full text-sm mt-1" value={node.display_name || ""} onChange={(e) => onPatch(node.id, { display_name: e.target.value })} placeholder="e.g. Pre-season block" data-testid="node-display-name" />
          </label>
        </div>

        <label className="block mt-3">
          <span className="text-[11px] text-zinc-500">Objective</span>
          <input className="fl-input w-full text-sm mt-1" value={node.objective || ""} onChange={(e) => onPatch(node.id, { objective: e.target.value })} data-testid="node-objective" />
        </label>

        {/* dates */}
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div>
            <div className="text-[11px] text-zinc-500">Dates (inclusive)</div>
            <div className="text-sm text-zinc-200 mt-1" data-testid="node-dates-detail">{fmtDate(node.start_date)} – {fmtDate(node.end_date)}</div>
            <div className="text-[11px] text-zinc-500">{durationDays(node.start_date, node.end_date)} days</div>
          </div>
          <button className="fl-btn-ghost text-xs" onClick={() => onDateEdit(node)} data-testid="node-date-edit"><CalendarDays size={12} /> Edit period dates</button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">Changing dates never alters exercise doses. You’ll preview the impact before anything moves.</p>

        {/* type-specific select + info */}
        {fieldCfg && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-zinc-500">{fieldCfg.label}</span>
              {currentEntry && <InfoButton entry={currentEntry} evidence={meta?.evidence} testid={`info-${fieldCfg.field}`} />}
            </div>
            <select className="fl-input text-sm mt-1 w-full sm:w-80" value={node[fieldCfg.field] || ""} onChange={(e) => onPatch(node.id, { [fieldCfg.field]: e.target.value })} data-testid={`node-${fieldCfg.field}`}>
              <option value="">Not set</option>
              {refGroup.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
        )}

        {/* mesocycle also carries a loading pattern */}
        {level === "mesocycle" && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-zinc-500">Loading pattern</span>
              <InfoButton entry={(meta?.reference?.loading_pattern || []).find((r) => r.key === node.loading_pattern)} evidence={meta?.evidence} testid="info-loading_pattern" />
            </div>
            <select className="fl-input text-sm mt-1 w-full sm:w-80" value={node.loading_pattern || ""} onChange={(e) => onPatch(node.id, { loading_pattern: e.target.value })} data-testid="node-loading_pattern">
              <option value="">Not set</option>
              {(meta?.reference?.loading_pattern || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
        )}

        {/* roadmap clinical phase link (micro/period) */}
        {(level === "microcycle" || level === "mesocycle") && (
          <RoadmapLink patientId={patientId} node={node} onPatch={onPatch} nav={nav} />
        )}
        </div>)}
      </div>

      {/* children */}
      <ChildArea level={level} node={node} childrenOf={childrenOf} nodeById={nodeById} meta={meta} programme={programme} flags={flags}
                 onPatch={onPatch} onDrill={onDrill} onLoad={onLoad} onAddChild={onAddChild} onDuplicate={onDuplicate} onRemove={onRemove}
                 onAddPlanExercise={onAddPlanExercise} onUpdatePlanExercise={onUpdatePlanExercise} onRemovePlanExercise={onRemovePlanExercise} progress={progress} nav={nav} />
    </div>
  );
}

/* ================= Roadmap link ================= */
function RoadmapLink({ patientId, node, onPatch, nav }) {
  const [phases, setPhases] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/roadmaps/patient/${patientId}`);
        const rm = (r.data || [])[0];
        setPhases(rm?.phases || []);
      } catch { setPhases([]); }
    })();
  }, [patientId]);
  return (
    <div className="mt-4 border-t border-white/10 pt-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <GitBranch size={13} className="text-[#f47a20]" />
          <span className="text-[11px] text-zinc-500">Linked clinical phase (roadmap)</span>
        </div>
        <button className="text-[11px] text-[#f47a20] hover:underline" onClick={() => nav(`/clinician/patient/${patientId}?tab=cases`)} data-testid="view-roadmap-link">View roadmap →</button>
      </div>
      <select className="fl-input text-sm mt-1 w-full sm:w-80" value={node.clinical_phase_id || ""} onChange={(e) => onPatch(node.id, { clinical_phase_id: e.target.value || null })} data-testid="node-clinical-phase">
        <option value="">Not linked</option>
        {(phases || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
      </select>
      <p className="text-[11px] text-zinc-500 mt-1">Clinical advancement stays criteria-based in the roadmap. Linking a phase here does not grant clearance.</p>
    </div>
  );
}

/* ================= Child area (periods / micros / days / sessions / units) ================= */
function ChildArea({ level, node, childrenOf, nodeById, meta, programme, flags, onPatch, onDrill, onLoad, onAddChild, onDuplicate, onRemove, onAddPlanExercise, onUpdatePlanExercise, onRemovePlanExercise, progress, nav }) {
  const kids = childrenOf(node.id);
  const childType = { macrocycle: "period", period: "mesocycle", mesocycle: "microcycle", annual: "macrocycle", multiannual: "annual", microcycle: "day" }[level];
  if (level === "microcycle") {
    return <MicroDays micro={node} childrenOf={childrenOf} meta={meta} programme={programme} onPatch={onPatch} onLoad={onLoad} onAddChild={onAddChild} onDuplicate={onDuplicate} onRemove={onRemove}
                      onAddPlanExercise={onAddPlanExercise} onUpdatePlanExercise={onUpdatePlanExercise} onRemovePlanExercise={onRemovePlanExercise} progress={progress} nav={nav} />;
  }
  if (!childType) return null;
  const childKids = kids.filter((k) => k.type === childType);
  const mode = (level === "multiannual" || level === "annual") ? "timeline" : "gantt";
  return (
    <GanttView parent={node} childType={childType} childKids={childKids} mode={mode}
               childrenOf={childrenOf} onDrill={onDrill} onAddChild={onAddChild} progress={progress} />
  );
}

/* ================= Timeline / Gantt visual view (Stage B) ================= */
function GanttView({ parent, childType, childKids, mode, childrenOf, onDrill, onAddChild, progress }) {
  const total = durationDays(parent.start_date, parent.end_date) || 1;
  const kids = [...childKids].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const span = (c) => {
    const left = datePct(c.start_date, parent.start_date, total);
    const endPct = datePct(c.end_date, parent.start_date, total);
    const width = Math.max(5, Math.min(100 - left, (endPct - left) + (100 / total)));
    return { left, width };
  };
  const ticks = axisTicks(parent.start_date, parent.end_date, 4);
  return (
    <div className="fl-card p-4" data-testid={`child-list-${childType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{TYPE_LABEL[childType]}s · {kids.length}</div>
        <button className="fl-btn-ghost text-xs" onClick={() => onAddChild(parent, childType)} data-testid={`add-${childType}`}>+ Add {childType}</button>
      </div>
      {kids.length === 0 ? (
        <div className="text-xs text-zinc-500">None yet.</div>
      ) : (
        <div data-testid={`${mode}-view-${childType}`}>
          <div className="relative h-4 mb-1">
            {ticks.map((t, i) => (
              <div key={i} className="absolute top-0 text-[9px] text-zinc-600 whitespace-nowrap" style={{ left: `${t.pct}%`, transform: i === 0 ? "none" : i === ticks.length - 1 ? "translateX(-100%)" : "translateX(-50%)" }}>{t.label}</div>
            ))}
          </div>
          {mode === "timeline" ? (() => {
            // Stack overlapping siblings into separate lanes so none hide each other.
            const laneEnds = [];
            const laneOf = kids.map((c) => {
              const s = c.start_date || "";
              let lane = laneEnds.findIndex((end) => end < s);
              if (lane === -1) lane = laneEnds.length;
              laneEnds[lane] = c.end_date || s;
              return lane;
            });
            const laneCount = Math.max(1, laneEnds.length);
            const laneH = 34;
            return (
              <div className="relative rounded-lg border border-white/10 bg-white/[0.02]" style={{ height: laneCount * laneH + 12 }}>
                {kids.map((c, i) => {
                  const { left, width } = span(c);
                  const col = blockColor(c, i);
                  const gc = childrenOf(c.id).length;
                  return (
                    <button key={c.id} onClick={() => onDrill(c.type, c.id)} title={`${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}`}
                      className="absolute rounded-md border px-2 py-1 overflow-hidden text-left hover:brightness-125 transition-[filter,transform] hover:-translate-y-0.5"
                      style={{ left: `${left}%`, width: `${width}%`, top: laneOf[i] * laneH + 6, height: laneH - 4, background: col.bg, borderColor: col.border }}
                      data-testid={`gantt-block-${c.id}`}>
                      <div className="text-[11px] font-semibold truncate leading-tight" style={{ color: col.text }}>{c.display_name || c.name}</div>
                      <div className="text-[9px] text-zinc-400 truncate leading-tight flex items-center gap-1">{durationDays(c.start_date, c.end_date)}d · {gc} child<ProgressBadge p={progress?.[c.id]} /></div>
                    </button>
                  );
                })}
              </div>
            );
          })() : (
            <div className="space-y-1.5">
              {kids.map((c, i) => {
                const { left, width } = span(c);
                const col = blockColor(c, i);
                const gc = childrenOf(c.id).length;
                return (
                  <div key={c.id} className="relative h-9 rounded-md bg-white/[0.02]" data-testid={`gantt-row-${c.id}`}>
                    <button onClick={() => onDrill(c.type, c.id)} title={`${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}`}
                      className="absolute top-0.5 bottom-0.5 rounded-md border px-2 flex items-center gap-2 overflow-hidden hover:brightness-125 transition-[filter]"
                      style={{ left: `${left}%`, width: `${width}%`, background: col.bg, borderColor: col.border }}
                      data-testid={`gantt-block-${c.id}`}>
                      <span className="text-[11px] font-semibold truncate" style={{ color: col.text }}>{c.display_name || c.name}</span>
                      <span className="text-[9px] text-zinc-400 whitespace-nowrap hidden sm:inline">{durationDays(c.start_date, c.end_date)}d · {gc} child</span>
                      <ProgressBadge p={progress?.[c.id]} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-[10px] text-zinc-600 mt-2">Click a block to open it. Bars are positioned by their dates within {parent.display_name || parent.name}.</div>
        </div>
      )}
    </div>
  );
}

/* ================= Microcycle → 7-day week grid → selected day detail ================= */
function MicroDays({ micro, childrenOf, meta, programme, onPatch, onLoad, onAddChild, onDuplicate, onRemove, onAddPlanExercise, onUpdatePlanExercise, onRemovePlanExercise, progress, nav }) {
  const days = childrenOf(micro.id).filter((d) => d.type === "day");
  const exOptions = (programme?.exercises || []).map((e) => ({ id: e.id, name: e.exercise?.name || e.library_key || "Exercise", sets: e.sets, reps: e.reps, weight: e.weight, rest: e.rest_seconds }));
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysISO(micro.start_date, i)), [micro.start_date]);
  const dayByDate = {};
  days.forEach((d) => { if (d.start_date) dayByDate[d.start_date] = d; });
  const sessionsOf = (day) => (day ? childrenOf(day.id).filter((s) => s.type === "session") : []);
  const dayAU = (day) => sessionsOf(day).reduce((sum, s) => sum + ((s.planned_minutes && s.target_rpe) ? s.planned_minutes * s.target_rpe : 0), 0);
  const firstWith = weekDates.map((iso) => dayByDate[iso]).find((d) => d && sessionsOf(d).length > 0);
  const firstDay = weekDates.map((iso) => dayByDate[iso]).find((d) => d) || days[0];
  const [selId, setSel] = useState((firstWith || firstDay)?.id || null);
  const selectedDay = days.find((d) => d.id === selId) || null;
  const addDayOn = (iso) => { const id = onAddChild({ ...micro, start_date: iso, end_date: iso }, "day"); if (id) setSel(id); };

  return (
    <div className="fl-card p-4" data-testid="micro-days">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">Week · {days.length} training day(s)</div>
        <button className="fl-btn-ghost text-xs" onClick={() => { const id = onAddChild(micro, "day"); if (id) setSel(id); }} data-testid="add-day">+ Add day</button>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mt-3" data-testid="micro-week-grid">
        {weekDates.map((iso) => {
          const day = dayByDate[iso];
          const ss = sessionsOf(day);
          const au = dayAU(day);
          const isSel = day && day.id === selId;
          const wd = new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
          const dd = new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
          return (
            <button key={iso} onClick={() => (day ? setSel(day.id) : addDayOn(iso))}
              className={`rounded-lg border p-1.5 sm:p-2 min-h-[94px] text-left transition flex flex-col ${isSel ? "border-white/25 bg-[#f47a20]/10" : day ? "border-white/10 hover:border-white/25" : "border-dashed border-white/10 hover:border-white/25"}`}
              data-testid={`week-col-${iso}`}>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-500">{wd}</div>
              <div className="text-[10px] sm:text-[11px] text-zinc-300 font-semibold">{dd}</div>
              {day ? (
                <>
                  <div className="mt-1 space-y-0.5 flex-1 overflow-hidden">
                    {ss.length === 0 && <div className="text-[9px] text-zinc-600">No session</div>}
                    {ss.slice(0, 3).map((s) => <div key={s.id} className="text-[9px] text-zinc-400 truncate rounded bg-white/5 px-1 py-0.5">{s.name || "Session"}</div>)}
                    {ss.length > 3 && <div className="text-[9px] text-zinc-600">+{ss.length - 3} more</div>}
                  </div>
                  <div className="text-[10px] text-[#f8a766] mt-1 flex items-center gap-1" data-testid={`week-col-au-${iso}`}>{au > 0 ? `${Math.round(au)} AU` : "— AU"}<ProgressBadge p={progress?.[day.id]} /></div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[10px] text-zinc-600">+ day</div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay ? (
        <DayDetail key={selectedDay.id} day={selectedDay} childrenOf={childrenOf} meta={meta} exOptions={exOptions}
          onPatch={onPatch} onLoad={onLoad} onAddChild={onAddChild} onDuplicate={onDuplicate} onRemove={onRemove}
          onAddPlanExercise={onAddPlanExercise} onUpdatePlanExercise={onUpdatePlanExercise} onRemovePlanExercise={onRemovePlanExercise} />
      ) : (
        <div className="text-xs text-zinc-500 mt-3">No training days yet — tap a day above to add one.</div>
      )}
      <p className="text-[11px] text-zinc-500 mt-3">Per-day AU = Σ (planned minutes × target session RPE) for that day's sessions. Resistance volume counts working sets. Actual load needs a completed session with a reported RPE and is shown separately under “Load”.</p>
    </div>
  );
}

/* ================= Selected-day detail (sessions / units / embedded builder) ================= */
function DayDetail({ day, childrenOf, meta, exOptions, onPatch, onLoad, onAddChild, onDuplicate, onRemove, onAddPlanExercise, onUpdatePlanExercise, onRemovePlanExercise }) {
  const sessions = childrenOf(day.id).filter((s) => s.type === "session");
  return (
    <div className="mt-3 rounded-lg border border-white/10 p-3" data-testid={`day-${day.id}`}>
      <div className="text-sm font-semibold text-zinc-200">{dayLabel(day.start_date)} <span className="text-[11px] text-zinc-500 font-normal">· {fmtDate(day.start_date)}</span>
        <button className="fl-btn-ghost text-[11px] ml-2" onClick={() => onAddChild(day, "session")} data-testid={`add-session-${day.id}`}>+ Session</button>
        <button className="fl-btn-ghost text-[11px]" onClick={() => onDuplicate(day)} data-testid={`dup-day-${day.id}`}>Duplicate</button>
        <button className="fl-btn-ghost text-[11px] text-red-300" onClick={() => onRemove(day)} data-testid={`remove-day-${day.id}`}>Remove</button>
      </div>
      {sessions.length === 0 && <div className="text-xs text-zinc-500 mt-2">No sessions on this day yet.</div>}
      {sessions.map((sess) => {
        const units = childrenOf(sess.id).filter((u) => u.type === "unit");
        return (
          <div key={sess.id} className="mt-2 rounded-md bg-white/[0.02] p-3" data-testid={`session-${sess.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <input className="fl-input text-xs py-1 w-40" value={sess.name || ""} onChange={(e) => onPatch(sess.id, { name: e.target.value })} data-testid={`session-name-${sess.id}`} />
              <label className="text-[11px] text-zinc-500 flex items-center gap-1"><Clock size={11} /> min
                <input type="number" className="fl-input text-xs py-1 w-16 ml-1" value={sess.planned_minutes ?? ""} onChange={(e) => onPatch(sess.id, { planned_minutes: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`session-min-${sess.id}`} />
              </label>
              <label className="text-[11px] text-zinc-500 flex items-center gap-1">target RPE
                <input type="number" min="0" max="10" step="0.5" className="fl-input text-xs py-1 w-16 ml-1" value={sess.target_rpe ?? ""} onChange={(e) => onPatch(sess.id, { target_rpe: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`session-rpe-${sess.id}`} />
              </label>
              <span className="text-[11px] text-[#f8a766]">Projected {(sess.planned_minutes && sess.target_rpe) ? Math.round(sess.planned_minutes * sess.target_rpe) : "—"} AU</span>
              <button className="fl-btn-ghost text-[11px] ml-auto" onClick={() => onAddChild(sess, "unit")} data-testid={`add-unit-${sess.id}`}>+ Unit</button>
              <button className="fl-btn-ghost text-[11px]" onClick={() => onDuplicate(sess)} data-testid={`dup-session-${sess.id}`}>Duplicate</button>
              <button className="fl-btn-ghost text-[11px] text-red-300" onClick={() => onRemove(sess)} data-testid={`remove-session-${sess.id}`}>Remove</button>
              <button className="fl-btn-ghost text-[11px]" onClick={() => onLoad(sess)} data-testid={`session-load-${sess.id}`}>Load</button>
            </div>
            <div className="mt-2 space-y-2">
              {units.map((u) => (
                <div key={u.id} className="rounded border border-white/10 p-2" data-testid={`unit-${u.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input className="fl-input text-xs py-1 w-40" value={u.name || ""} onChange={(e) => onPatch(u.id, { name: e.target.value })} data-testid={`unit-name-${u.id}`} />
                    <select className="fl-input text-xs py-1 w-36" value={u.unit_category || ""} onChange={(e) => onPatch(u.id, { unit_category: e.target.value })} data-testid={`unit-cat-${u.id}`}>
                      <option value="">Category…</option>
                      {(meta?.unit_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span className="text-[11px] text-zinc-500">{(u.exercise_refs || []).length} exercise(s)</span>
                    <button className="fl-btn-ghost text-[11px] text-red-300 ml-auto" onClick={() => onRemove(u)} data-testid={`remove-unit-${u.id}`}>Remove</button>
                  </div>
                  <UnitExercises unit={u} exOptions={exOptions} onPatch={onPatch}
                    onAddPlanExercise={onAddPlanExercise} onUpdatePlanExercise={onUpdatePlanExercise} onRemovePlanExercise={onRemovePlanExercise} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ================= Embedded Exercise Builder (per training unit) ================= */
function UnitExercises({ unit, exOptions, onPatch, onAddPlanExercise, onUpdatePlanExercise, onRemovePlanExercise }) {
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const refs = unit.exercise_refs || [];
  const attached = exOptions.filter((e) => refs.includes(e.id));
  const available = exOptions.filter((e) => !refs.includes(e.id));

  useEffect(() => {
    if (!q.trim()) { setHits([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/exercises?q=${encodeURIComponent(q)}&limit=20`);
        if (active) setHits(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch { if (active) setHits([]); }
      finally { if (active) setSearching(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const numOrText = (v) => (v === "" ? null : (isNaN(Number(v)) ? v : Number(v)));

  return (
    <div className="mt-2" data-testid={`unit-builder-${unit.id}`}>
      {attached.length === 0 && <div className="text-[11px] text-zinc-500">No exercises in this unit yet.</div>}
      {attached.map((ex) => (
        <div key={ex.id} className="flex items-center gap-1.5 flex-wrap text-[11px] mb-1.5" data-testid={`unit-ex-row-${unit.id}-${ex.id}`}>
          <CheckCircle2 size={11} className="text-[#f47a20] shrink-0" />
          <b className="text-zinc-200">{ex.name}</b>
          <input className="fl-input text-[11px] py-0.5 w-12" defaultValue={ex.sets ?? ""} placeholder="sets" title="sets"
            onBlur={(e) => onUpdatePlanExercise(ex.id, { sets: numOrText(e.target.value) })} data-testid={`unit-ex-sets-${ex.id}`} />
          <span className="text-zinc-600">×</span>
          <input className="fl-input text-[11px] py-0.5 w-14" defaultValue={ex.reps ?? ""} placeholder="reps" title="reps"
            onBlur={(e) => onUpdatePlanExercise(ex.id, { reps: numOrText(e.target.value) })} data-testid={`unit-ex-reps-${ex.id}`} />
          <input className="fl-input text-[11px] py-0.5 w-24" defaultValue={ex.weight ?? ""} placeholder="load" title={'load e.g. "40 kg" or "70% 1RM"'}
            onBlur={(e) => onUpdatePlanExercise(ex.id, { weight: e.target.value || null })} data-testid={`unit-ex-weight-${ex.id}`} />
          <input className="fl-input text-[11px] py-0.5 w-14" defaultValue={ex.rest ?? ""} placeholder="rest s" title="rest seconds"
            onBlur={(e) => onUpdatePlanExercise(ex.id, { rest_seconds: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`unit-ex-rest-${ex.id}`} />
          <button className="text-zinc-400 hover:text-zinc-200 ml-1" title="Detach from this unit (keeps it on the programme)"
            onClick={() => onPatch(unit.id, { exercise_refs: refs.filter((x) => x !== ex.id) })} data-testid={`unit-ex-detach-${unit.id}-${ex.id}`}>detach</button>
          <button className="text-red-400 hover:text-red-300" title="Remove from programme"
            onClick={() => onRemovePlanExercise(ex.id)} data-testid={`unit-ex-remove-${ex.id}`}><Trash2 size={11} /></button>
        </div>
      ))}

      {available.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          <span className="text-[11px] text-zinc-500 self-center">Reuse:</span>
          {available.map((ex) => (
            <button key={ex.id} type="button" onClick={() => onPatch(unit.id, { exercise_refs: [...refs, ex.id] })}
              className="text-[11px] px-2 py-1 rounded-full border border-white/15 text-zinc-300 hover:border-[#f47a20]/50 transition"
              data-testid={`unit-reuse-${unit.id}-${ex.id}`}>
              <Dumbbell size={10} className="inline mr-1" />{ex.name}
            </button>
          ))}
        </div>
      )}

      <button className="fl-btn-ghost text-[11px] mt-1.5" onClick={() => setShowAdd((v) => !v)} data-testid={`unit-add-lib-${unit.id}`}>
        <Plus size={11} /> Add exercise from library
      </button>
      {showAdd && (
        <div className="mt-1.5 rounded border border-white/10 p-2" data-testid={`unit-lib-panel-${unit.id}`}>
          <div className="flex items-center gap-1.5">
            <Search size={12} className="text-zinc-500" />
            <input autoFocus className="fl-input text-[11px] py-1 flex-1" placeholder="Search the exercise library…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`unit-lib-search-${unit.id}`} />
          </div>
          <div className="mt-1.5 max-h-48 overflow-y-auto space-y-1">
            {searching && <div className="text-[11px] text-zinc-500">Searching…</div>}
            {!searching && q.trim() && hits.length === 0 && <div className="text-[11px] text-zinc-500">No matches.</div>}
            {hits.map((h) => (
              <button key={h.id} type="button" onClick={() => { onAddPlanExercise(unit.id, h); setShowAdd(false); setQ(""); }}
                className="w-full text-left text-[11px] px-2 py-1 rounded border border-white/10 hover:border-[#f47a20]/50 text-zinc-200 transition"
                data-testid={`unit-lib-hit-${unit.id}-${h.id}`}>
                {h.name} {h.body_region ? <span className="text-zinc-500">· {h.body_region}</span> : null}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= Date-change modal (preview → apply) ================= */
function DateChangeModal({ plan, node, onClose, onApplied }) {
  const [start, setStart] = useState(node.start_date || "");
  const [end, setEnd] = useState(node.end_date || "");
  const [scope, setScope] = useState("container_only");
  const [reason, setReason] = useState("");
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/periodisation/${plan.id}/preview-date-change`, { node_id: node.id, new_start: start, new_end: end, scope });
      setImpact(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not preview."); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/periodisation/${plan.id}/apply-date-change`, { node_id: node.id, new_start: start, new_end: end, scope, reason });
      onApplied(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply."); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="date-change-modal">
      <div className="fl-card-elevated w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="fl-heading text-lg">Edit period dates · {node.display_name || node.name}</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="date-modal-close"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] text-zinc-500">Start date</span>
              <input type="date" className="fl-input w-full text-sm mt-1" value={start} onChange={(e) => { setStart(e.target.value); setImpact(null); }} data-testid="date-modal-start" /></label>
            <label className="block"><span className="text-[11px] text-zinc-500">End date · inclusive</span>
              <input type="date" className="fl-input w-full text-sm mt-1" value={end} onChange={(e) => { setEnd(e.target.value); setImpact(null); }} data-testid="date-modal-end" /></label>
          </div>
          <label className="block"><span className="text-[11px] text-zinc-500">How should descendants be handled?</span>
            <select className="fl-input w-full text-sm mt-1" value={scope} onChange={(e) => { setScope(e.target.value); setImpact(null); }} data-testid="date-modal-scope">
              <option value="container_only">Keep child dates (change this period only)</option>
              <option value="shift_descendants">Shift all children by the same offset</option>
            </select>
          </label>

          {!impact ? (
            <button className="fl-btn w-full justify-center text-sm" onClick={preview} disabled={busy || !start || !end} data-testid="date-modal-preview">
              {busy ? "Checking…" : "Preview impact"}
            </button>
          ) : (
            <div className="rounded-lg border border-white/10 p-3 space-y-2 text-xs" data-testid="date-modal-impact">
              <div className="text-zinc-300">{fmtDate(impact.old.start)}–{fmtDate(impact.old.end)} ({impact.old.duration_days}d) → <b className="text-[#f8a766]">{fmtDate(impact.new.start)}–{fmtDate(impact.new.end)} ({impact.new.duration_days}d)</b></div>
              <div className="text-zinc-400">Affected child periods/sessions: <b>{impact.affected_descendant_count}</b></div>
              <div className="text-zinc-400">{impact.descendants_action}</div>
              {impact.out_of_parent && <div className="text-amber-300">⚠ New dates fall outside the parent period.</div>}
              {impact.sibling_conflicts.length > 0 && <div className="text-amber-300">⚠ Overlaps: {impact.sibling_conflicts.map((c) => c.name).join(", ")}</div>}
              <div className={impact.completed_or_locked_sessions ? "text-emerald-300" : "text-zinc-500"}>{impact.completed_note}</div>
              <div className="text-zinc-500">{impact.prescriptions_note}</div>
              <label className="block pt-1"><span className="text-[11px] text-zinc-500">Reason (recorded in history)</span>
                <input className="fl-input w-full text-sm mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="date-modal-reason" /></label>
              <div className="flex gap-2 pt-1">
                <button className="fl-btn flex-1 justify-center text-sm" onClick={apply} disabled={busy} data-testid="date-modal-apply">{busy ? "Applying…" : "Apply change"}</button>
                <button className="fl-btn-ghost text-sm" onClick={() => setImpact(null)} data-testid="date-modal-reedit">Re-edit</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ================= Load modal ================= */
function LoadModal({ data, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="load-modal">
      <div className="fl-card-elevated w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="fl-heading text-lg">Load &amp; volume · {data.node?.display_name || data.node?.name}</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="load-modal-close"><X size={20} /></button>
        </div>
        {data.loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">Computing…</div>
        ) : (
          <div className="p-4 space-y-3 overflow-y-auto">
            {data.frequency && (
              <div className="rounded-lg border border-white/10 p-3 grid grid-cols-3 gap-2" data-testid="load-frequency">
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-400">Sessions</div><div className="fl-heading text-xl mt-0.5">{data.frequency.scheduled_sessions}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-400">Training days</div><div className="fl-heading text-xl mt-0.5">{data.frequency.training_days}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-400">Period</div><div className="fl-heading text-xl mt-0.5">{data.frequency.period_days ?? "—"}<span className="text-sm text-zinc-500"> d</span></div></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-white/10 p-3" data-testid="load-projected">
                <div className="text-[10px] uppercase tracking-widest text-[#f47a20]">Target · session load</div>
                <div className="fl-heading text-2xl mt-1">{data.projected.session_load_au} <span className="text-sm text-zinc-500">AU</span></div>
                <div className="text-[11px] text-zinc-500 mt-1">{data.projected.sessions_counted} session(s)</div>
                <div className="text-[10px] text-zinc-600 mt-1">{data.projected.method}</div>
              </div>
              <div className="rounded-lg border border-white/10 p-3" data-testid="load-actual">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">Reported · session load</div>
                <div className="fl-heading text-2xl mt-1">{data.actual.available ? data.actual.session_load_au : "—"} <span className="text-sm text-zinc-500">AU</span></div>
                <div className="text-[11px] text-zinc-500 mt-1">{data.actual.sessions_counted} reported{data.actual.sessions_missing_rpe ? ` · ${data.actual.sessions_missing_rpe} missing RPE` : ""}</div>
                <div className="text-[11px] text-[#f8a766] mt-0.5" data-testid="load-reporting-label">{data.actual.reporting_label}</div>
                <div className="text-[10px] text-zinc-600 mt-1">{data.actual.method}</div>
              </div>
            </div>
            <div className="rounded-lg border border-white/10 p-3" data-testid="load-volume">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">Resistance volume</div>
              <div className="flex items-end gap-4 mt-1">
                <div><div className="fl-heading text-xl">{data.resistance_volume.working_sets}</div><div className="text-[10px] text-zinc-500">working sets</div></div>
                <div><div className="fl-heading text-xl">{data.resistance_volume.volume_load_available ? `${data.resistance_volume.volume_load_kg}` : "—"}</div><div className="text-[10px] text-zinc-500">kg·reps volume-load</div></div>
              </div>
              {(data.resistance_volume.rows || []).length > 0 && (
                <div className="mt-2 border-t border-white/10 pt-2 space-y-0.5" data-testid="load-volume-rows">
                  {data.resistance_volume.rows.map((r, i) => (
                    <div key={i} className="text-[11px] text-zinc-300 flex justify-between gap-2">
                      <span><b className="text-zinc-200">{r.exercise}</b> {r.sets ? `${r.sets}×${r.reps ?? "?"}` : ""}{r.weight ? ` · ${r.weight}` : ""}</span>
                      <span className="text-zinc-400">{r.volume_load_kg != null ? `${r.volume_load_kg} kg·reps` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.resistance_volume.unquantified_count > 0 && (
                <div className="text-[10px] text-amber-300/80 mt-1">{data.resistance_volume.unquantified_count} exercise(s) not weight-quantified (band / bodyweight) — excluded from kg·reps. %1RM prescriptions appear under relative intensity.</div>
              )}
              <div className="text-[10px] text-zinc-600 mt-1">{data.resistance_volume.method}</div>
            </div>
            {data.intensity?.length > 0 && (
              <div className="rounded-lg border border-white/10 p-3" data-testid="load-intensity">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Relative intensity (per exercise)</div>
                {data.intensity.map((r, i) => (
                  <div key={i} className="text-[11px] text-zinc-300 flex justify-between gap-2 py-0.5">
                    <span><b className="text-zinc-200">{r.exercise}</b> {r.sets ? `${r.sets}×${r.reps ?? "?"}` : ""} {r.weight ? `· ${r.weight}` : ""} {r.rest_seconds ? `· ${r.rest_seconds}s` : ""} {r.side && r.side !== "both" ? `· ${r.side}` : ""}</span>
                    <span className={r.pct_1rm != null ? "text-[#f8a766]" : "text-zinc-600"} data-testid={`load-intensity-pct-${i}`}>{r.pct_1rm != null ? `${r.pct_1rm}% 1RM` : "—"}</span>
                  </div>
                ))}
                <div className="text-[10px] text-zinc-600 mt-1">{data.intensity_note}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ================= Publish review (before/after diff) ================= */
function PublishReviewModal({ data, onBack, onConfirm }) {
  const loading = data?.loading;
  const ctx = data?.context;
  const struct = data?.structure;
  const presc = data?.prescriptions || [];
  const noChanges = !loading && ctx && ctx.changes.length === 0 && presc.length === 0 &&
    (struct.added.length + struct.removed.length + struct.rescheduled.length) === 0;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }} onClick={onBack} data-testid="publish-review-modal">
      <div className="fl-card-elevated w-full max-w-xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="fl-heading text-lg">Review changes before publishing</div>
          <button onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="publish-review-close"><X size={20} /></button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">Building review…</div>
        ) : (
          <div className="p-4 space-y-4 overflow-y-auto">
            {!data.has_published && <div className="text-xs text-amber-300/90" data-testid="publish-review-first">First publish — everything below becomes visible to the athlete.</div>}
            {noChanges && <div className="text-sm text-zinc-300" data-testid="publish-review-nochanges">No changes since the last publish. Publishing again will re-confirm the current plan.</div>}

            {/* patient-visible context — ALWAYS shown so the clinician sees exactly what the athlete's roadmap will read */}
            <div data-testid="publish-review-context">
              <div className="text-[10px] uppercase tracking-widest text-[#f47a20] mb-1">Roadmap the athlete will see</div>
              <div className="text-xs text-zinc-300">Phase: <b className="text-zinc-100">{String(ctx.after?.phase ?? "—")}</b>{ctx.after?.goal ? ` — ${ctx.after.goal}` : ""}</div>
              <div className="text-xs text-zinc-300">Microcycle: <b className="text-zinc-100">{String(ctx.after?.microcycle ?? "—")}</b> <span className="text-zinc-500">{ctx.after?.microcycle_dates || ""}</span></div>
              {ctx.changes.length > 0 ? (
                <div className="mt-1 border-t border-white/10 pt-1">
                  {ctx.changes.map((c, i) => (
                    <div key={i} className="text-[11px] text-zinc-400">{c.field}: <span className="text-zinc-500 line-through">{String(c.before ?? "—")}</span> → <b className="text-[#f8a766]">{String(c.after ?? "—")}</b></div>
                  ))}
                </div>
              ) : <div className="text-[11px] text-zinc-500 mt-1">No change to the athlete’s roadmap view.</div>}
            </div>

            {/* structure */}
            {(struct.added.length + struct.removed.length + struct.rescheduled.length) > 0 && (
              <div data-testid="publish-review-structure">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Plan structure</div>
                {struct.added.map((s, i) => <div key={`a${i}`} className="text-xs text-emerald-300">+ {s.label} <span className="text-zinc-500">{s.dates}</span></div>)}
                {struct.removed.map((s, i) => <div key={`r${i}`} className="text-xs text-red-300">− {s.label}</div>)}
                {struct.rescheduled.map((s, i) => <div key={`m${i}`} className="text-xs text-zinc-300">↻ {s.label}: <span className="text-zinc-500">{s.before}</span> → <b className="text-[#f8a766]">{s.after}</b></div>)}
              </div>
            )}

            {/* prescriptions */}
            {presc.length > 0 && (
              <div data-testid="publish-review-prescriptions">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Exercises &amp; prescriptions</div>
                {presc.map((p, i) => (
                  <div key={i} className="rounded-lg border border-white/10 p-2 mb-2">
                    <div className="text-xs text-zinc-200 font-semibold flex items-center gap-2">
                      {p.exercise}
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${p.status === "added" ? "bg-emerald-500/15 text-emerald-300" : p.status === "removed" ? "bg-red-500/15 text-red-300" : "bg-white/10 text-zinc-300"}`}>{p.status}</span>
                      {p.patient_visible ? <span className="text-[9px] text-[#f8a766]">athlete-visible</span> : <span className="text-[9px] text-zinc-500">clinician-only</span>}
                    </div>
                    {p.placement_changed && <div className="text-[11px] text-zinc-400 mt-0.5">Scheduled: <span className="text-zinc-500">{(p.placement_before || ["—"]).join(", ")}</span> → <b className="text-[#f8a766]">{(p.placement_after || ["—"]).join(", ")}</b></div>}
                    {p.field_changes.map((f, j) => <div key={j} className="text-[11px] text-zinc-400">{f.field}: <span className="text-zinc-500 line-through">{String(f.before ?? "—")}</span> → <b className="text-zinc-200">{String(f.after ?? "—")}</b></div>)}
                  </div>
                ))}
              </div>
            )}

            <ul className="text-[11px] text-zinc-500 list-disc pl-4 space-y-0.5">
              {(data.notes || []).map((n, i) => <li key={i}>{n}</li>)}
            </ul>

            <div className="flex gap-2 pt-1">
              <button className="fl-btn flex-1 justify-center text-sm" onClick={onConfirm} data-testid="publish-review-confirm">Confirm &amp; publish</button>
              <button className="fl-btn-ghost text-sm" onClick={onBack} data-testid="publish-review-back">Back to editing</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ================= Principles & models ================= */
function PrinciplesModal({ meta, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="principles-modal">
      <div className="fl-card-elevated w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="fl-heading text-lg">Principles &amp; models</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="principles-close"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-zinc-500">Educational planning frameworks — not automatic recovery timers, readiness scores or overtraining diagnoses.</p>
          {(meta?.principles || []).map((p) => (
            <div key={p.key} className="rounded-lg border border-white/10 p-3" data-testid={`principle-${p.key}`}>
              <div className="text-sm font-bold text-[#f47a20]">{p.label}</div>
              <p className="text-xs text-zinc-200 mt-1">{p.what}</p>
              <p className="text-[11px] text-amber-300/90 mt-1">{p.caveat}</p>
              <div className="text-[10px] text-zinc-600 mt-1">Sources: {(p.sources || []).map((s) => {
                const ev = meta?.evidence?.[s];
                return ev?.url ? <a key={s} href={ev.url} target="_blank" rel="noreferrer" className="text-[#f47a20] hover:underline mr-2">{s}</a> : <span key={s} className="mr-2">{s}</span>;
              })}</div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ===========================================================================
 * Stage C — Rule-based draft generator (NO AI/LLM). Surfaces read-only athlete
 * context, lets the clinician pick a versioned template + inputs, previews the
 * transparent rationale, and applies the draft into the planner for review.
 * ======================================================================== */
function GenerateDraftModal({ plan, onClose, onApply }) {
  const [ctx, setCtx] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState(null);
  const [form, setForm] = useState({ start_date: "", total_weeks: 12, days_per_week: 3, session_minutes: 50, event_date: "" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, t] = await Promise.all([
          api.get(`/periodisation/${plan.id}/athlete-context`),
          api.get(`/periodisation/generator/templates`),
        ]);
        if (!alive) return;
        setCtx(c.data);
        setTemplates(t.data.templates || []);
        setTplId((prev) => prev ?? ((t.data.templates || [])[0]?.id || null));
        const s = c.data.suggested || {};
        setForm((f) => (f.start_date ? f : { ...f, start_date: s.start_date || "", total_weeks: s.total_weeks || 12, days_per_week: s.days_per_week || 3, session_minutes: s.session_minutes || 50 }));
      } catch (e) {
        if (!alive) return;
        toast.error("Could not load the draft generator.");
        onClose();
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const tpl = templates.find((t) => t.id === tplId);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setPreview(null); };

  const runPreview = async () => {
    if (!tplId || !form.start_date) { toast.warning("Pick a template and a start date."); return; }
    setBusy(true);
    try {
      const res = await api.post(`/periodisation/${plan.id}/generate-draft`, {
        template_id: tplId, template_version: tpl?.version,
        start_date: form.start_date, total_weeks: Number(form.total_weeks),
        days_per_week: Number(form.days_per_week), session_minutes: Number(form.session_minutes) || 50,
        event_date: form.event_date || null,
      });
      setPreview(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate the draft."); }
    finally { setBusy(false); }
  };

  const c = preview?.summary?.counts;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="generate-draft-modal">
      <div className="fl-card-elevated w-full max-w-2xl p-5 max-h-[92vh] overflow-auto" style={{ background: "var(--fl-bg-2)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#f47a20] font-semibold"><Wand2 size={16} /> Generate a periodisation draft</div>
            <p className="text-[11px] text-zinc-500 mt-1 max-w-md">Transparent, rule-based templates — no AI. The draft is unsaved: review and edit it, then Save &amp; Publish as usual. Exercises, loads and reps are never invented.</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="generate-draft-close"><X size={18} /></button>
        </div>

        {loading && <div className="py-10 text-center text-sm text-zinc-400" data-testid="generate-draft-loading">Loading athlete context…</div>}

        {!loading && ctx && (
          <>
            {/* Athlete context (read-only) */}
            <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="generate-draft-context">
              <div className="fl-label text-[11px] text-zinc-400 mb-2">Athlete context (read-only)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                <div><div className="text-zinc-500">Clinical roadmap</div><div className="text-zinc-200 mt-0.5">{ctx.roadmap ? (ctx.roadmap.current_phase || "Active") : "None linked"}</div></div>
                <div><div className="text-zinc-500">Active cases</div><div className="text-zinc-200 mt-0.5">{ctx.active_cases?.length ? ctx.active_cases.map((x) => x.title).join(", ") : "None"}</div></div>
                <div><div className="text-zinc-500">Programme</div><div className="text-zinc-200 mt-0.5">{ctx.programme?.exercise_count ?? 0} exercise(s)</div></div>
              </div>
            </div>

            {/* Template picker */}
            <div className="mt-4">
              <div className="fl-label text-[11px] text-zinc-400 mb-2">Template</div>
              <div className="space-y-2" data-testid="generate-draft-templates">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => { setTplId(t.id); setPreview(null); }}
                    className={`w-full text-left rounded-lg border p-3 transition ${tplId === t.id ? "border-[#f47a20] bg-[#f47a20]/10" : "border-white/10 hover:border-white/25"}`}
                    data-testid={`generate-template-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{t.name}</span>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-white/15 rounded-full px-1.5 py-0.5">v{t.version}</span>
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-1">{t.summary}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Inputs */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label className="text-[11px] text-zinc-400">Start date
                <input type="date" className="fl-input w-full text-sm mt-1" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="generate-start-date" />
              </label>
              <label className="text-[11px] text-zinc-400">Total weeks
                <input type="number" min="1" max="52" className="fl-input w-full text-sm mt-1" value={form.total_weeks} onChange={(e) => set("total_weeks", e.target.value)} data-testid="generate-total-weeks" />
              </label>
              <label className="text-[11px] text-zinc-400">Days / week
                <input type="number" min="1" max="7" className="fl-input w-full text-sm mt-1" value={form.days_per_week} onChange={(e) => set("days_per_week", e.target.value)} data-testid="generate-days-per-week" />
              </label>
              <label className="text-[11px] text-zinc-400">Session minutes
                <input type="number" min="5" max="240" className="fl-input w-full text-sm mt-1" value={form.session_minutes} onChange={(e) => set("session_minutes", e.target.value)} data-testid="generate-session-minutes" />
              </label>
              <label className="text-[11px] text-zinc-400 col-span-2">Target / event date (optional)
                <input type="date" className="fl-input w-full text-sm mt-1" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="generate-event-date" />
              </label>
            </div>

            {/* Rules of the chosen template */}
            {tpl && (
              <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="fl-label text-[11px] text-zinc-400 mb-1.5">How this template builds the plan</div>
                <ul className="text-[11px] text-zinc-300 space-y-1 list-disc pl-4" data-testid="generate-template-rules">
                  {tpl.rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button onClick={runPreview} disabled={busy} className="fl-btn-ghost text-sm disabled:opacity-50" data-testid="generate-preview-btn">
                <Sparkles size={13} /> {busy ? "Generating…" : preview ? "Regenerate" : "Preview draft"}
              </button>
            </div>

            {/* Preview: rationale + counts */}
            {preview && (
              <div className="mt-4 rounded-lg border border-[#f47a20]/40 bg-[#f47a20]/5 p-3" data-testid="generate-preview">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#f8a766]"><CheckCircle2 size={15} /> Draft ready for review</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3 text-center">
                  {[["period", "Periods"], ["mesocycle", "Mesos"], ["microcycle", "Weeks"], ["day", "Days"], ["session", "Sessions"], ["unit", "Units"]].map(([k, lbl]) => (
                    <div key={k} className="rounded-md bg-white/[0.03] border border-white/10 py-1.5" data-testid={`generate-count-${k}`}>
                      <div className="text-sm font-bold text-white">{c?.[k] ?? 0}</div>
                      <div className="text-[9px] uppercase tracking-wide text-zinc-500">{lbl}</div>
                    </div>
                  ))}
                </div>
                <div className="fl-label text-[11px] text-zinc-400 mt-3 mb-1">Why the draft looks like this</div>
                <ol className="text-[11px] text-zinc-300 space-y-1.5" data-testid="generate-rationale">
                  {preview.rationale.map((r, i) => (
                    <li key={i} className="border-l-2 border-[#f47a20]/40 pl-2">
                      <span className="font-semibold text-zinc-100">{r.rule}.</span> <span className="text-zinc-400">{r.because}.</span> <span className="text-[#f8a766]">→ {r.result}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-[11px] text-amber-200/90 mt-3 flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> Applying replaces the current unsaved plan structure. Nothing reaches the athlete until you Save and Publish. Completed sessions are never moved.</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => onApply(preview.draft_nodes)} className="fl-btn text-sm flex-1 justify-center" data-testid="generate-apply-btn">Apply to planner for review</button>
                  <button onClick={onClose} className="fl-btn-ghost text-sm" data-testid="generate-cancel-btn">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

